import { t as localeText } from "@/i18n/translate";
import { getMediaToolCatalog } from "@/features/media-tools";
import { videoToolCapabilities } from "../lib/videoToolCapabilities";
import { creditBalance } from "@/api/credit-balance";
import { api, currentSpaceID } from "@/api/client";
import type { components } from "@/api/contract";
import { unwrap } from "@/api/errors";
import { generationVisualPreview } from "virtual:afterform-generation-visual-preview";
import type {
  V2Asset,
  V2Capability,
  V2Generation,
  V2GenerationCreate,
  V2GenerationQuote,
  V2GenerationQuoteRequest,
  V2Model,
  V2Project,
  V2ProjectContext,
  V2ProjectContextPatch,
  V2ProjectCreativeDecisionCreate,
  V2ProjectCreativeDecisionList,
  V2ProjectCreativeDecisionResult,
  V2ProjectLineageGraph,
  V2Reusable,
  V2VideoDocument,
  V2VideoRender,
  V2VideoRenderCreate,
} from "../types";

type ContractV2GenerationCreate = components["schemas"]["V2GenerationCreate"];
type ContractV2GenerationQuoteRequest =
  components["schemas"]["V2GenerationQuoteRequest"];
type ContractV2GenerationSourceSelection =
  components["schemas"]["V2GenerationSourceSelection"];

function contractGenerationSourceSelections(
  selections: Array<{ id: string; role: string }> | undefined,
): ContractV2GenerationSourceSelection[] | undefined {
  return selections?.map((selection) => ({
    ...selection,
    role: selection.role as ContractV2GenerationSourceSelection["role"],
  }));
}

export async function getImageCapabilities(): Promise<{
  items: V2Capability[];
  productGenerationReady: boolean;
  catalogRevision: string;
  capturedSurfaceRevision: string;
}> {
  if (generationVisualPreview) {
    return {
      items: generationVisualPreview.capabilities.filter(
        (item) => item.media_type === "image",
      ),
      productGenerationReady: true,
      catalogRevision: "visual-preview-product-catalog-v1",
      capturedSurfaceRevision: "visual-preview-captured-surfaces-v1",
    };
  }
  const response = unwrap(await api.GET("/v1/capabilities"));
  const ready = response.product_generation_ready.image;
  return {
    // The full Image catalog remains visible even when no route is admitted.
    // Each item carries its own qualified, quote-required, or unsupported gate.
    items: response.items.filter((item) => item.media_type === "image"),
    productGenerationReady: ready,
    catalogRevision: response.catalog_revision,
    capturedSurfaceRevision: response.captured_surface_revision,
  };
}

export async function getVideoCapabilities(operation?: string): Promise<{
  items: V2Capability[];
  productGenerationReady: boolean;
  catalogRevision: string;
  capturedSurfaceRevision: string;
}> {
  if (generationVisualPreview) {
    return {
      items: generationVisualPreview.capabilities.filter(
        (item) =>
          item.media_type === "video" &&
          (!operation || item.operation === operation),
      ),
      productGenerationReady: true,
      catalogRevision: "visual-preview-product-catalog-v1",
      capturedSurfaceRevision: "visual-preview-captured-surfaces-v1",
    };
  }
  const [response, tools] = await Promise.all([
    api.GET("/v1/capabilities").then(unwrap),
    getMediaToolCatalog("video"),
  ]);
  const toolCapabilities = videoToolCapabilities(tools.items);
  const toolIDs = new Set(toolCapabilities.map(item => item.product_model_id));
  return {
    items: [...toolCapabilities, ...response.items.filter(item => item.media_type === "video" && !toolIDs.has(item.product_model_id))].filter(item => !operation || item.operation === operation),
    productGenerationReady: response.product_generation_ready.video || toolCapabilities.length > 0,
    catalogRevision: response.catalog_revision,
    capturedSurfaceRevision: response.captured_surface_revision,
  };
}

export async function listVideoModels(operation?: string): Promise<V2Model[]> {
  const models = await listGlobalModels();
  return models.filter(
    (item) => item.media_type === "video" && (!operation || item.operation === operation),
  );
}

export async function listImageModels(): Promise<V2Model[]> {
  const models = await listGlobalModels();
  return models.filter((item) => item.media_type === "image");
}

/** Load the provider-neutral global model inventory once at the API boundary. */
export async function listGlobalModels(): Promise<V2Model[]> {
  if (generationVisualPreview) {
    return generationVisualPreview.models.filter((item) => item.kind === "model");
  }
  const response = unwrap(await api.GET("/v1/models"));
  return response.items.filter((item) => item.kind === "model");
}

export async function getGenerationQuote(
  command: V2GenerationQuoteRequest,
  signal?: AbortSignal,
): Promise<V2GenerationQuote> {
  return unwrap(
    await api.POST("/v1/generation-quotes", {
      body: {
        ...command,
        operation:
          command.operation as ContractV2GenerationQuoteRequest["operation"],
        source_assets: contractGenerationSourceSelections(
          command.source_assets,
        ),
      },
      signal,
    }),
  );
}

export async function enhanceGenerationPrompt(
  prompt: string,
  mediaType: "image" | "video" | "audio",
): Promise<string> {
  const result = unwrap(
    await api.POST("/v1/prompts/enhance", {
      body: { prompt, media_type: mediaType },
    }),
  );
  return result.prompt;
}

export async function listGenerationProjects(): Promise<V2Project[]> {
  return unwrap(
    await api.GET("/v1/projects", {
      params: { query: { limit: 100, status: "active" } },
    }),
  ).items;
}

export async function getProjectContext(
  projectID: string,
): Promise<V2ProjectContext> {
  return unwrap(
    await api.GET("/v1/projects/{id}/context", {
      params: { path: { id: projectID } },
    }),
  );
}

export async function patchProjectContext(
  projectID: string,
  command: V2ProjectContextPatch,
  idempotencyKey: string,
): Promise<V2ProjectContext> {
  return unwrap(
    await api.PATCH("/v1/projects/{id}/context", {
      params: {
        path: { id: projectID },
        header: { "Idempotency-Key": idempotencyKey },
      },
      body: command,
    }),
  );
}

export async function listProjectCreativeDecisions(
  projectID: string,
  cursor?: string,
): Promise<V2ProjectCreativeDecisionList> {
  return unwrap(
    await api.GET("/v1/projects/{id}/decisions", {
      params: {
        path: { id: projectID },
        query: { limit: 100, ...(cursor ? { cursor } : {}) },
      },
    }),
  );
}

export async function getProjectLineage(
  projectID: string,
): Promise<V2ProjectLineageGraph> {
  return unwrap(
    await api.GET("/v1/projects/{id}/lineage", {
      params: { path: { id: projectID } },
    }),
  );
}

export async function createProjectCreativeDecision(
  projectID: string,
  command: V2ProjectCreativeDecisionCreate,
  idempotencyKey: string,
): Promise<V2ProjectCreativeDecisionResult> {
  return unwrap(
    await api.POST("/v1/projects/{id}/decisions", {
      params: {
        path: { id: projectID },
        header: { "Idempotency-Key": idempotencyKey },
      },
      body: command,
    }),
  );
}

export async function createGeneration(
  command: V2GenerationCreate,
  idempotencyKey: string,
): Promise<V2Generation> {
  return creditBalance.submit(currentSpaceID(), idempotencyKey, command.accepted_cost_ceiling ?? 0, async () => unwrap(
    await api.POST("/v1/generations", {
      params: { header: { "Idempotency-Key": idempotencyKey } },
      body: {
        ...command,
        operation: command.operation as ContractV2GenerationCreate["operation"],
        source_assets: contractGenerationSourceSelections(
          command.source_assets,
        ),
      },
    }),
  ));
}

export async function reviseGeneration(
  generationID: string,
  command: V2GenerationCreate,
  idempotencyKey: string,
): Promise<V2Generation> {
  return creditBalance.submit(currentSpaceID(), idempotencyKey, command.accepted_cost_ceiling ?? 0, async () => unwrap(
    await api.POST("/v1/generations/{id}/revisions", {
      params: {
        path: { id: generationID },
        header: { "Idempotency-Key": idempotencyKey },
      },
      body: {
        ...command,
        operation: command.operation as ContractV2GenerationCreate["operation"],
        source_assets: contractGenerationSourceSelections(
          command.source_assets,
        ),
      },
    }),
  ));
}

export async function listImageGenerations(
  projectID?: string,
): Promise<V2Generation[]> {
  return unwrap(
    await api.GET("/v1/generations", {
      params: {
        query: {
          media_type: "image",
          limit: 30,
          ...(projectID ? { project_id: projectID } : {}),
        },
      },
    }),
  ).items;
}

export async function listVideoGenerations(
  projectID?: string,
): Promise<V2Generation[]> {
  const items: V2Generation[] = [];
  const ids = new Set<string>();
  const cursors = new Set<string>();
  let cursor: string | undefined;
  do {
    const page = unwrap(
      await api.GET("/v1/generations", {
        params: {
          query: {
            media_type: "video",
            limit: 100,
            ...(projectID ? { project_id: projectID } : {}),
            ...(cursor ? { cursor } : {}),
          },
        },
      }),
    );
    for (const item of page.items) {
      if (!ids.has(item.id)) {
        ids.add(item.id);
        items.push(item);
      }
    }
    cursor = page.next_cursor || undefined;
    if (cursor && cursors.has(cursor)) {
      throw new Error(
        localeText("interface.video_generation_pagination_returned_a_repeated_2c9c2cfc", { defaultValue: "Video generation pagination returned a repeated cursor." }),
      );
    }
    if (cursor) cursors.add(cursor);
  } while (cursor);
  return items;
}

export { getGeneration } from "@/api/generations";

export { getGenerationAuthority } from "@/api/generation-authority";

export async function getAsset(assetID: string): Promise<V2Asset> {
  return unwrap(
    await api.GET("/v1/assets/{id}", {
      params: { path: { id: assetID } },
    }),
  );
}

export async function listSelectableVideoStudioAssets(
  mediaType: "image" | "video" | "audio",
  projectID?: string,
): Promise<V2Asset[]> {
  const items: V2Asset[] = [];
  const ids = new Set<string>();
  const cursors = new Set<string>();
  let cursor: string | undefined;
  do {
    const page = unwrap(
      await api.GET("/v1/assets", {
        params: {
          query: {
            media_type: mediaType,
            status: "available",
            limit: 100,
            sort: "newest",
            ...(projectID ? { project_id: projectID } : {}),
            ...(cursor ? { cursor } : {}),
          },
        },
      }),
    );
    for (const item of page.items) {
      if (!ids.has(item.id)) {
        ids.add(item.id);
        items.push(item);
      }
    }
    cursor = page.next_cursor || undefined;
    if (cursor && cursors.has(cursor)) {
      throw new Error(localeText("interface.video_asset_pagination_returned_a_repeated_curso_ee8e25b3", { defaultValue: "Video Asset pagination returned a repeated cursor." }));
    }
    if (cursor) cursors.add(cursor);
  } while (cursor);
  return items;
}

export async function getGenerationVideoDocument(
  generationID: string,
): Promise<V2VideoDocument> {
  const document = unwrap(
    await api.GET("/v1/generations/{id}/video-document", {
      params: { path: { id: generationID }, query: { limit: 100 } },
    }),
  );
  const versions = [...document.versions];
  const ids = new Set(versions.map((version) => version.id));
  const cursors = new Set<string>();
  let cursor = document.versions_next_cursor || undefined;
  while (cursor) {
    if (cursors.has(cursor))
      throw new Error(localeText("interface.video_version_pagination_returned_a_repeated_cur_eafdb34e", { defaultValue: "Video version pagination returned a repeated cursor." }));
    cursors.add(cursor);
    const page = unwrap(
      await api.GET("/v1/video-documents/{id}", {
        params: {
          path: { id: document.id },
          query: { limit: 100, versions_cursor: cursor },
        },
      }),
    );
    if (page.id !== document.id)
      throw new Error(localeText("interface.video_version_pagination_changed_documents_be57f617", { defaultValue: "Video version pagination changed documents." }));
    for (const version of page.versions) {
      if (!ids.has(version.id)) {
        ids.add(version.id);
        versions.push(version);
      }
    }
    cursor = page.versions_next_cursor || undefined;
  }
  return { ...document, versions, versions_next_cursor: "" };
}

export async function acceptVideoVersion(
  documentID: string,
  versionID: string,
  expectedVersion: number,
  idempotencyKey: string,
): Promise<V2VideoDocument> {
  return unwrap(
    await api.POST("/v1/video-documents/{id}/versions/{version_id}/accept", {
      params: {
        path: { id: documentID, version_id: versionID },
        header: { "Idempotency-Key": idempotencyKey },
      },
      body: { expected_version: expectedVersion },
    }),
  );
}

export async function listVideoRenders(
  documentID: string,
  cursor?: string,
): Promise<{ items: V2VideoRender[]; nextCursor: string; cursor: string }> {
  const page = unwrap(
    await api.GET("/v1/video-documents/{id}/renders", {
      params: {
        path: { id: documentID },
        query: { limit: 25, ...(cursor ? { cursor } : {}) },
      },
    }),
  );
  if (page.next_cursor && page.next_cursor === cursor)
    throw new Error(localeText("interface.video_render_pagination_returned_a_repeated_curs_35d859a7", { defaultValue: "Video render pagination returned a repeated cursor." }));
  return {
    items: page.items,
    nextCursor: page.next_cursor || "",
    cursor: cursor ?? "",
  };
}

export async function createVideoRender(
  documentID: string,
  command: V2VideoRenderCreate,
): Promise<V2VideoRender> {
  return unwrap(
    await api.POST("/v1/video-documents/{id}/renders", {
      params: { path: { id: documentID } },
      body: command,
    }),
  );
}

export async function cancelVideoRender(
  documentID: string,
  renderID: string,
  expectedVersion: number,
  idempotencyKey: string,
): Promise<V2VideoRender> {
  return unwrap(
    await api.POST("/v1/video-documents/{id}/renders/{render_id}/cancel", {
      params: {
        path: { id: documentID, render_id: renderID },
        header: { "Idempotency-Key": idempotencyKey },
      },
      body: { expected_version: expectedVersion },
    }),
  );
}

export async function retryVideoRender(
  documentID: string,
  renderID: string,
  expectedVersion: number,
  idempotencyKey: string,
): Promise<V2VideoRender> {
  return unwrap(
    await api.POST("/v1/video-documents/{id}/renders/{render_id}/retry", {
      params: {
        path: { id: documentID, render_id: renderID },
        header: { "Idempotency-Key": idempotencyKey },
      },
      body: { expected_version: expectedVersion },
    }),
  );
}

export async function downloadVideoAsset(
  assetID: string,
): Promise<{ name: string; url: string }> {
  const response = unwrap(
    await api.POST("/v1/assets/bulk/download", {
      body: { asset_ids: [assetID] },
    }),
  );
  const download = response.items.find((item) => item.id === assetID);
  if (!download) throw new Error(localeText("interface.the_video_export_could_not_be_authorized_48bbc1a2", { defaultValue: "The Video export could not be authorized." }));
  return download;
}

export async function cancelGeneration(
  generation: V2Generation,
  idempotencyKey: string,
): Promise<V2Generation> {
  return unwrap(
    await api.POST("/v1/generations/{id}/cancel", {
      params: {
        path: { id: generation.id },
        header: { "Idempotency-Key": idempotencyKey },
      },
      body: { expected_version: generation.version },
    }),
  );
}

export async function retryGenerationParts(
  generation: V2Generation,
  partKeys: string[],
  idempotencyKey: string,
): Promise<V2Generation> {
  return unwrap(
    await api.POST("/v1/generations/{id}/retry", {
      params: {
        path: { id: generation.id },
        header: { "Idempotency-Key": idempotencyKey },
      },
      body: { expected_version: generation.version, part_keys: partKeys },
    }),
  );
}

export interface SelectableImageAssetPage {
  items: V2Asset[];
  nextCursor?: string;
}

export async function listSelectableImageAssets(
  cursor?: string,
  projectID?: string,
): Promise<SelectableImageAssetPage> {
  const response = unwrap(
    await api.GET("/v1/assets", {
      params: {
        query: {
          media_type: "image",
          status: "available",
          limit: 24,
          sort: "newest",
          ...(projectID ? { project_id: projectID } : {}),
          ...(cursor ? { cursor } : {}),
        },
      },
    }),
  );
  return { items: response.items, nextCursor: response.next_cursor };
}

export async function listCompatibleReusable(): Promise<V2Reusable[]> {
  return unwrap(
    await api.GET("/v1/reusable", {
      params: { query: { media_type: "image", operation: "generate" } },
    }),
  ).items;
}

export async function listCompatibleVideoReusable(
  operation: string,
): Promise<V2Reusable[]> {
  return unwrap(
    await api.GET("/v1/reusable", {
      params: { query: { media_type: "video", operation } },
    }),
  ).items;
}
